import {
  IStateService,
  PrimitiveType,
  StateEnum,
  TRegisteredState,
  UTILS,
} from '@aksia/infrastructure';
import { Injectable, computed, signal } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class StateService implements IStateService {
  registeredStates: Map<string, TRegisteredState> = new Map<
    string,
    TRegisteredState
  >();

  hasDirty = signal(false);
  hasDirtyAndAudited = signal(false);
  summary = signal('');
  state = signal(StateEnum.Pristine);

  stateExists(tag: string) {
    return this.registeredStates.has(tag);
  }

  getState(tag: string) {
    if (tag.startsWith('@')) {
      let hasDirty = this.registeredStates
        .entries()
        ?.some(
          (registeredState) =>
            this.containsUid(registeredState[0], tag) &&
            ([StateEnum.Deleted, StateEnum.Dirty].includes(
              registeredState[1].state,
            ) ||
              (registeredState[1].state === StateEnum.Created &&
                this.compareValues(
                  registeredState[1].pristineValue,
                  registeredState[1].currentValue,
                  registeredState[1].comparer,
                ))),
        );
      return hasDirty ? StateEnum.Dirty : StateEnum.Pristine;
    } else {
      let registeredState = this.registeredStates.get(tag);
      return !registeredState ? undefined : registeredState.state;
    }
  }

  getRegisteredState(tag: string): TRegisteredState | undefined {
    return this.registeredStates.get(tag);
  }

  registerState(
    tag: string,
    value: unknown,
    state: StateEnum = StateEnum.Pristine,
    comparer?: (a: any, b: any) => boolean,
    audited?: boolean,
  ) {
    if (!this.registeredStates.has(tag)) {
      this.registeredStates.set(tag, {
        pristineValue: UTILS.DATE.isDate(value)
          ? value
          : typeof value === 'object' && !Array.isArray(value)
            ? { ...value }
            : value,
        currentValue: UTILS.DATE.isDate(value)
          ? value
          : typeof value === 'object' && !Array.isArray(value)
            ? { ...value }
            : value,
        state: state,
        comparer: comparer,
        audited: audited,
      });
      this.updateFlags();
    }
  }

  resetState(tag: string, onlyDirty = false) {
    let tags = tag.startsWith('@')
      ? [...this.registeredStates.keys()].filter((registeredStateTag) =>
          this.containsUid(registeredStateTag, tag),
        )
      : [tag];
    tags.forEach((tag) => {
      let registeredState = this.registeredStates.get(tag);
      if (
        registeredState &&
        (!onlyDirty || registeredState.state === StateEnum.Dirty)
      ) {
        registeredState.pristineValue = registeredState.currentValue;
        registeredState.state = StateEnum.Pristine;
        this.registeredStates.set(tag, registeredState);
      }
    });
    this.updateFlags();
  }

  removeState(tag: string, hardDelete = false) {
    let tags = tag.startsWith('@')
      ? [...this.registeredStates.keys()].filter((registeredStateTag) =>
          this.containsUid(registeredStateTag, tag),
        )
      : [tag];
    tags.forEach((tag) => {
      let registeredState = this.registeredStates.get(tag);
      if (!registeredState) {
        return;
      }
      if (registeredState.state === StateEnum.Created || hardDelete) {
        this.registeredStates.delete(tag);
      } else {
        this.registeredStates.set(tag, {
          pristineValue: registeredState.currentValue,
          currentValue: registeredState.currentValue,
          state: StateEnum.Deleted,
        });
      }
    });

    this.updateFlags();
  }

  clearStates() {
    this.registeredStates.clear();
    this.hasDirty.set(false);
    this.hasDirtyAndAudited.set(false);
    this.state.set(StateEnum.Pristine);
    this.summary.set('');
  }

  updateState(tag: string, value: unknown) {
    let tags = tag.startsWith('@')
      ? [...this.registeredStates.keys()].filter((registeredStateTag) =>
          this.containsUid(registeredStateTag, tag),
        )
      : [tag];
    tags.forEach((tag) => {
      let registeredState = this.registeredStates.get(tag);
      if (registeredState) {
        registeredState.currentValue = value;
        registeredState.state =
          registeredState.state === StateEnum.Created
            ? StateEnum.Created
            : this.compareValues(
                  registeredState.pristineValue,
                  registeredState.currentValue,
                  registeredState.comparer,
                )
              ? StateEnum.Dirty
              : StateEnum.Pristine;
        this.registeredStates.set(tag, registeredState);
      }
    });

    this.updateFlags();
  }

  // NOTE: every uid-scoped operation matches through here, never with
  // `includes`. A key carries its uid after the last `@`, nested uids joined by
  // `_`, and a substring test cannot tell a uid from the start of a longer one:
  // `@3` is inside `Offset Rate@357`. The entity saved first used to reset by
  // its own uid and take with it every key whose uid merely began the same way,
  // so the entity saved after it looked untouched and its edit was dropped
  // without a word. Comparing segment by segment keeps the scoping that is
  // meant — a uid still matches its own children, `@357` covers `@357_394` —
  // and drops the collision.
  private containsUid(registeredStateKey: string, tag: string) {
    let searchFrom = tag.lastIndexOf('@') + 1;
    let registeredFrom = registeredStateKey.lastIndexOf('@') + 1;

    while (true) {
      let searchSeparator = tag.indexOf('_', searchFrom);
      let registeredSeparator = registeredStateKey.indexOf('_', registeredFrom);

      let searchTo = searchSeparator === -1 ? tag.length : searchSeparator;
      let registeredTo =
        registeredSeparator === -1
          ? registeredStateKey.length
          : registeredSeparator;

      let searchLength = searchTo - searchFrom;
      let registeredLength = registeredTo - registeredFrom;

      if (
        searchLength === 0 ||
        registeredLength === 0 ||
        searchLength !== registeredLength
      ) {
        return false;
      }

      for (let i = 0; i < searchLength; i++) {
        if (
          tag.charCodeAt(searchFrom + i) !==
          registeredStateKey.charCodeAt(registeredFrom + i)
        ) {
          return false;
        }
      }

      if (searchSeparator === -1) {
        return true;
      }

      if (registeredSeparator === -1) {
        return false;
      }

      searchFrom = searchSeparator + 1;
      registeredFrom = registeredSeparator + 1;
    }
  }

  private updateFlags() {
    this.hasDirty.set(
      [...this.registeredStates.values()].some((registeredState) =>
        [StateEnum.Dirty, StateEnum.Created, StateEnum.Deleted].includes(
          registeredState.state,
        ),
      ),
    );

    this.hasDirtyAndAudited.set(
      [...this.registeredStates.values()].some(
        (registeredState) =>
          registeredState.audited &&
          [StateEnum.Dirty, StateEnum.Created, StateEnum.Deleted].includes(
            registeredState.state,
          ),
      ),
    );

    this.state.set(this.hasDirty() ? StateEnum.Dirty : StateEnum.Pristine);

    this.summary.set(
      [...this.registeredStates.entries()]
        .filter((registeredState) =>
          [StateEnum.Dirty, StateEnum.Created, StateEnum.Deleted].includes(
            registeredState[1].state,
          ),
        )
        .map(
          (registeredState) =>
            `${registeredState?.[0]}: ${registeredState?.[1].currentValue}`,
        )
        .join('\n\r'),
    );
  }

  private compareValues(
    pristineValue: unknown,
    currentValue: unknown,
    comparer?: (a: any, b: any) => boolean,
  ) {
    return comparer
      ? !comparer(pristineValue, currentValue)
      : !(
          UTILS.OBJECT.isNil(currentValue) && UTILS.OBJECT.isNil(pristineValue)
        ) && currentValue !== pristineValue;
  }
}
